import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { AlertTriangle, Mail, Calendar } from 'lucide-react';
import SEO from '../../components/SEO';

const RiskDisclosurePage = () => {
  const lastUpdated = '17 Aralık 2025';

  return (
    <div className="min-h-screen bg-brand-dark py-12">
      <SEO 
        title="Risk Uyarısı"
        description="Borsamex platformu risk uyarısı. Trading ve yatırım riskleri hakkında önemli bilgiler."
        url="/legal/risk"
      />
      
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 px-3 py-1 bg-red-500/10 text-red-400 border-red-500/20">
            <AlertTriangle className="h-3 w-3 mr-1" /> Önemli
          </Badge>
          <h1 className="text-4xl font-bold text-white mb-4">
            Risk Uyarısı
          </h1>
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Calendar className="h-4 w-4" />
            <span>Son güncelleme: {lastUpdated}</span>
          </div>
        </div>

        {/* Critical Warning Box */}
        <Card className="border-red-500/30 bg-red-500/5 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-8 w-8 text-red-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-red-400 mb-2">Önemli Uyarı</h2>
                <p className="text-slate-300">
                  Bu platform üzerinden sunulan stratejiler, sinyaller ve içerikler <strong className="text-white">yatırım tavsiyesi değildir</strong>. 
                  Trading ve yatırım faaliyetleri önemli riskler içerir ve yatırdığınız sermayenin tamamını kaybedebilirsiniz. 
                  Kaybetmeyi göze alamayacağınız paralarla işlem yapmayın.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        <Card className="border-slate-800 bg-slate-900/50">
          <CardContent className="p-8 prose prose-invert max-w-none">
            
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Genel Risk Bildirimi</h2>
              <p className="text-slate-300 mb-4">
                Finansal piyasalarda işlem yapmak, yüksek düzeyde risk içermektedir. Borsamex platformunu 
                kullanmadan önce aşağıdaki riskleri dikkatli bir şekilde değerlendirmeniz gerekmektedir:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Piyasa koşulları hızla değişebilir ve önemli kayıplara yol açabilir</li>
                <li>Geçmiş performans, gelecekteki sonuçların garantisi değildir</li>
                <li>Yatırdığınız sermayenin tamamını kaybetme riski bulunmaktadır</li>
                <li>Kaldıraçlı işlemler kayıpları artırabilir</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. Platform Hizmetleri Hakkında</h2>
              <div className="p-4 rounded-lg bg-slate-800/50 mb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Borsamex Ne Değildir?</h3>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Yatırım danışmanlığı veya portföy yönetimi hizmeti</li>
                  <li>Lisanslı bir aracı kurum veya broker</li>
                  <li>Garanti getiri vaat eden bir platform</li>
                  <li>SPK veya herhangi bir regülatör tarafından lisanslanmış kuruluş</li>
                </ul>
              </div>
              <div className="p-4 rounded-lg bg-slate-800/50">
                <h3 className="text-lg font-semibold text-white mb-2">Borsamex Nedir?</h3>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Strateji oluşturma ve test etme aracı</li>
                  <li>Kullanıcıların kendi stratejilerini paylaşabildiği bir platform</li>
                  <li>Eğitim ve bilgilendirme amaçlı araçlar</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Strateji ve Sinyal Riskleri</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li><strong className="text-white">Backtest Sınırlamaları:</strong> Geçmiş verilerle yapılan testler, 
                gelecekteki performansı garanti etmez. Gerçek piyasa koşulları farklılık gösterebilir.</li>
                <li><strong className="text-white">Strateji Geliştirici Riskleri:</strong> Platform üzerinde strateji 
                yayınlayan kullanıcılar profesyonel yatırım danışmanı değildir.</li>
                <li><strong className="text-white">Sinyal Gecikmeleri:</strong> Teknik nedenlerle sinyaller gecikmeli 
                ulaşabilir, bu durum sonuçları etkileyebilir.</li>
                <li><strong className="text-white">Overfitting:</strong> Stratejiler geçmiş verilere aşırı uyum 
                sağlamış olabilir ve gelecekte aynı performansı göstermeyebilir.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Kripto Para Riskleri</h2>
              <p className="text-slate-300 mb-4">
                Kripto para piyasaları özellikle yüksek volatiliteye sahiptir:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Fiyatlar kısa sürede %50 veya daha fazla değişebilir</li>
                <li>Düzenleyici belirsizlikler mevcuttur</li>
                <li>Likidite sorunları yaşanabilir</li>
                <li>Güvenlik riskleri (hack, dolandırıcılık) bulunmaktadır</li>
                <li>7/24 açık piyasalar sürekli izleme gerektirebilir</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Teknik Riskler</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Platform erişim kesintileri yaşanabilir</li>
                <li>İnternet bağlantı sorunları işlemleri etkileyebilir</li>
                <li>Veri gecikmeleri veya hataları oluşabilir</li>
                <li>Üçüncü taraf hizmet sağlayıcıların (borsa API'leri vb.) kesintileri olabilir</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">6. Sorumluluk Reddi</h2>
              <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                <p className="text-slate-300 mb-4">
                  <strong className="text-yellow-200">Datex Bilişim AŞ ve Borsamex:</strong>
                </p>
                <ul className="list-disc list-inside text-slate-300 space-y-2">
                  <li>Herhangi bir getiri garantisi vermez</li>
                  <li>Kullanıcıların yatırım kararlarından sorumlu değildir</li>
                  <li>Strateji geliştiricilerin performansını garanti etmez</li>
                  <li>Piyasa kayıplarından sorumlu tutulamaz</li>
                  <li>Teknik aksaklıklardan doğan zararlardan sorumlu değildir</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. Öneriler</h2>
              <p className="text-slate-300 mb-4">
                Platformumuzu kullanırken aşağıdaki önerileri dikkate almanızı tavsiye ederiz:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Sadece kaybetmeyi göze alabileceğiniz paralarla işlem yapın</li>
                <li>Yatırım kararlarınızı vermeden önce kendi araştırmanızı yapın</li>
                <li>Gerekirse lisanslı bir yatırım danışmanına başvurun</li>
                <li>Risk yönetimi stratejileri uygulayın (stop-loss vb.)</li>
                <li>Tek bir stratejiye veya varlığa bağımlı olmayın, portföyünüzü çeşitlendirin</li>
                <li>Duygusal kararlar almaktan kaçının</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">8. Yasal Uyarı</h2>
              <p className="text-slate-300">
                Bu platform ve içerikleri, Sermaye Piyasası Kurulu (SPK) veya herhangi bir düzenleyici 
                kurum tarafından onaylanmamıştır. Platform üzerinden sunulan hiçbir içerik, profesyonel 
                yatırım tavsiyesi olarak değerlendirilmemelidir. Yatırım kararlarınız tamamen kendi 
                sorumluluğunuzdadır.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. İletişim</h2>
              <p className="text-slate-300 mb-4">
                Risk uyarısı hakkında sorularınız için bizimle iletişime geçebilirsiniz:
              </p>
              <div className="p-4 rounded-lg bg-slate-800/50">
                <p className="text-slate-300 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <strong className="text-white">E-posta:</strong> info@borsamex.com
                </p>
              </div>
            </section>

          </CardContent>
        </Card>

        {/* Acknowledgment */}
        <Card className="border-slate-700 bg-slate-800/50 mt-8">
          <CardContent className="p-6 text-center">
            <p className="text-slate-300">
              Borsamex platformunu kullanarak, bu risk uyarısını okuduğunuzu, anladığınızı ve 
              kabul ettiğinizi beyan etmiş olursunuz.
            </p>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm">
          <Link to="/legal/privacy" className="text-slate-400 hover:text-primary transition-colors">
            Gizlilik Politikası
          </Link>
          <span className="text-slate-600">•</span>
          <Link to="/legal/terms" className="text-slate-400 hover:text-primary transition-colors">
            Kullanım Koşulları
          </Link>
          <span className="text-slate-600">•</span>
          <Link to="/legal/cookies" className="text-slate-400 hover:text-primary transition-colors">
            Çerez Politikası
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RiskDisclosurePage;
